/* Auto-extracted from nexgrow_v2.jsx — i18n / language data */
import { createContext } from "react";

export const LangCtx = createContext("en");

/* The ~8 MB translation dictionary is ONLY needed for non-English languages (tr() returns
   early for "en"). Keep it out of the initial bundle and load it on demand the first time a
   non-EN language activates — English (the default and only indexed language) never fetches
   it. Cuts the main JS bundle roughly in half for the default experience. */
let DICT = {};
const dictPromises = {};          // lang -> in-flight/resolved load promise
const dictSubs = new Set();
export function onDictLoaded(fn) { dictSubs.add(fn); return () => dictSubs.delete(fn); }
/* Load ONE language's dictionary on demand. Webpack emits a separate chunk per dict.<lang>.js,
   so a non-EN visitor downloads only the active language (~0.4 MB gz) instead of all six (~3 MB gz).
   English never loads any dict (tr() returns the source string). */
export function ensureDict(lang) {
  const l = lang || CURRENT_LANG;
  if (!l || l === "en") return Promise.resolve(DICT);
  if (dictPromises[l]) return dictPromises[l];
  dictPromises[l] = import(/* webpackChunkName: "dict-[request]" */ `./dict.${l}.js`)
    .then(m => { DICT[l] = m.default || {}; dictSubs.forEach(fn => { try { fn(); } catch (_) {} }); return DICT; })
    .catch(() => { delete dictPromises[l]; return DICT; });
  return dictPromises[l];
}

/* Free-text translator: looks up an exact English string in the per-language
   dictionary, falling back to the original English if there is no entry.
   Used for body content & UI literals that aren't keyed in the T table below. */
export function tr(lang, s) {
  if (s == null || lang === "en") return s;
  const m = DICT[lang];
  if (!m) return s;
  const hit = m[s];
  return hit == null ? s : hit;
}

/* Current language tracker — set by <App> on every render so the global
   createElement patch (which has no access to React context) can translate
   string children / text attributes against the active language. */
export let CURRENT_LANG = "en";
export function setCurrentLang(l) { CURRENT_LANG = l; if (l && l !== "en") ensureDict(l); }
/* Translate against the current language. */
export function trc(s) { return typeof s === "string" ? tr(CURRENT_LANG, s) : s; }

const T = {
  en: {
    /* navbar */
    contactUs:"Contact Us", marketplace:"Marketplace",
    services:"Services", industries:"Industries", caseStudies:"Case Studies",
    blog:"Blog", company:"Company",
    search_placeholder:"Search pages, services, industries…",
    quick_links:"Quick Links",
    /* hero */
    hero_h1_1:"All-in-One Marketing",
    hero_h1_2:"for Your",
    hero_h1_3:"Crypto, Forex",
    hero_h1_4:"&",
    hero_h1_6:"Project",
    hero_sub:"Full-Cycle Agency and Affiliate Marketplace covering KOL marketing, PR, Community management, Fundraising and Web3 development — ensure your brand stands out and achieves global growth.",
    stat1_label:"satisfied clients",
    stat2_label:"industry leaders trust us",
    stat3_label:"years in marketing",
    cta_proposal:"Get a Free Proposal",
    cta_call:"Book a Call",
    /* strips */
    trusted_by:"Trusted by industry-leading companies",
    online_label:"Online — we reply within minutes",
    /* page heroes */
    mp_h1:"Find the right partner<br/>for your campaign",
    mp_sub:"Browse trusted service providers — Polymarket, BC.Game, 1win and more — across Crypto, Forex and iGaming. Filter by industry, payout model and region.",
    cases_h1:"Results that<br/>speak for themselves",
    cases_hsub:"Real campaigns. Real clients. Real numbers. Here's what we've delivered across Crypto, Forex and iGaming.",
    clients_h1:"500+ projects.<br/>Some names you know.",
    clients_sub:"We've worked with the biggest names in Crypto, Forex, and iGaming — as well as the next generation of projects you'll be hearing about soon.",
    sv_h1a:"Full-spectrum marketing",
    sv_h1b:"for three industries only",
    sv_sub:"We specialize exclusively in Crypto, Forex and iGaming. Every service we offer has been refined across 50+ real campaigns in these verticals — not adapted from general marketing templates.",
    ind_h1a:"Deep expertise across",
    ind_h1b:"high-stakes industries",
    ind_sub:"We don't do general marketing. We specialize exclusively in Crypto, Forex and iGaming — the most competitive, regulated and fastest-moving industries in the world. Choose your vertical below.",
    /* sections */
    our_services:"Our Services",
    services_title:"Everything your project<br/>needs to grow",
    services_sub:"We cover the full growth stack — from audience building and user acquisition to fundraising campaigns, token launches, and Web3 development.",
    industries_tag:"Industries",
    industries_title:"Built for high-stakes industries",
    industries_sub:"We help brands grow in markets where competition is intense, trust is hard to earn, and every user matters.",
    cases_tag:"Case Studies",
    cases_title:"Results we're proud of",
    cases_sub:"Real campaigns. Real clients. Real numbers.",
    how_tag:"How We Work",
    how_title:"From first call to<br/>measurable growth",
    how_sub:"A transparent process built for competitive markets — from research and strategy to launch, reporting, and scale.",
    cta_big_title:"Ready to grow your project?",
    cta_big_sub:"Leave your details and our team will reply within 24 hours with a tailored strategy for your project.",
    /* footer */
    footer_desc:"Full-spectrum marketing agency for Web3, Crypto, Forex and iGaming. Results-driven. Industry-specialized.",
    /* nav labels */
    nav_growth:"Growth & Awareness",
    nav_brand:"Brand & Community",
    nav_verticals:"Our Verticals",
    nav_explore:"Explore",
    nav_content:"Content Types",
    nav_about_col:"About",
  },
  uk: {
    /* navbar */
    contactUs:"Зв'язатись", marketplace:"Маркетплейс",
    services:"Послуги", industries:"Індустрії", caseStudies:"Кейси",
    blog:"Блог", company:"Компанія",
    search_placeholder:"Пошук сторінок, послуг, індустрій…",
    quick_links:"Швидкі посилання",
    /* hero */
    hero_h1_1:"Маркетинг під ключ",
    hero_h1_2:"для вашого",
    hero_h1_3:"Крипто, Форекс",
    hero_h1_4:"&",
    hero_h1_6:"проєкту",
    hero_sub:"Агенція повного циклу та партнерський маркетплейс, що охоплює KOL-маркетинг, PR, керування спільнотою, фандрейзинг та Web3-розробку — зробіть ваш бренд помітним і досягніть глобального зростання.",
    stat1_label:"задоволених клієнтів",
    stat2_label:"лідерів галузі довіряють нам",
    stat3_label:"роки в маркетингу",
    cta_proposal:"Отримати пропозицію",
    cta_call:"Замовити дзвінок",
    /* strips */
    trusted_by:"Нам довіряють лідери ринку",
    online_label:"Онлайн — відповідаємо за хвилини",
    /* page heroes */
    mp_h1:"Знайдіть правильного партнера<br/>для вашої кампанії",
    mp_sub:"Переглядайте перевірених постачальників послуг — Polymarket, BC.Game, 1win та інші — у Крипто, Форекс та iGaming. Фільтруйте за індустрією, моделлю виплат і регіоном.",
    cases_h1:"Результати, що<br/>говорять самі за себе",
    cases_hsub:"Реальні кампанії. Реальні клієнти. Реальні цифри. Ось що ми зробили в Крипто, Форекс та iGaming.",
    clients_h1:"500+ проєктів.<br/>Імена, які ви знаєте.",
    clients_sub:"Ми працювали з найбільшими іменами в Крипто, Форекс та iGaming — а також із новим поколінням проєктів, про які ви скоро почуєте.",
    sv_h1a:"Маркетинг повного спектра",
    sv_h1b:"лише для трьох індустрій",
    sv_sub:"Ми спеціалізуємося виключно на Крипто, Форекс та iGaming. Кожна наша послуга відточена в 50+ реальних кампаніях у цих вертикалях — а не адаптована із загальних маркетингових шаблонів.",
    ind_h1a:"Глибока експертиза в",
    ind_h1b:"індустріях високих ставок",
    ind_sub:"Ми не займаємось загальним маркетингом. Ми спеціалізуємося виключно на Крипто, Форекс та iGaming — найконкурентніших, найбільш регульованих і найшвидших індустріях у світі. Оберіть свою вертикаль нижче.",
    /* sections */
    our_services:"Наші Послуги",
    services_title:"Все, що потрібно вашому<br/>проєкту для зростання",
    services_sub:"Ми покриваємо повний стек зростання — від побудови аудиторії та залучення користувачів до фандрейзинг-кампаній, запуску токенів і Web3-розробки.",
    industries_tag:"Індустрії",
    industries_title:"Створено для індустрій високих ставок",
    industries_sub:"Ми допомагаємо брендам зростати там, де конкуренція нещадна, довіру доводиться заслуговувати, а кожен користувач на вагу золота.",
    cases_tag:"Кейси",
    cases_title:"Результати, якими ми пишаємось",
    cases_sub:"Реальні кампанії. Реальні клієнти. Реальні цифри.",
    how_tag:"Як ми працюємо",
    how_title:"Від першого дзвінка до<br/>вимірюваного зростання",
    how_sub:"Прозорий процес для конкурентних ринків — від дослідження та стратегії до запуску, звітності й масштабування.",
    cta_big_title:"Готові до зростання?",
    cta_big_sub:"Залиште свої дані — ми відповімо протягом 24 годин з індивідуальною стратегією для вашого проєкту.",
    /* footer */
    footer_desc:"Повноцінне маркетингове агентство для Web3, Крипто, Форекс та iGaming. Орієнтовані на результат. Вузька спеціалізація.",
    /* nav labels */
    nav_growth:"Зростання та охоплення",
    nav_brand:"Бренд та спільнота",
    nav_verticals:"Наші вертикалі",
    nav_explore:"Дослідити",
    nav_content:"Типи контенту",
    nav_about_col:"Про нас",
  },
  ru: {
    contactUs:"Связаться", marketplace:"Маркетплейс",
    services:"Услуги", industries:"Индустрии", caseStudies:"Кейсы",
    blog:"Блог", company:"Компания",
    search_placeholder:"Поиск страниц, услуг, индустрий…",
    quick_links:"Быстрые ссылки",
    hero_h1_1:"Маркетинг под ключ",
    hero_h1_2:"для вашего",
    hero_h1_3:"Крипто, Форекс",
    hero_h1_4:"&",
    hero_h1_6:"проекта",
    hero_sub:"Агентство полного цикла и партнёрский маркетплейс, охватывающий KOL-маркетинг, PR, управление сообществом, фандрайзинг и Web3-разработку — сделайте ваш бренд заметным и достигните глобального роста.",
    stat1_label:"довольных клиентов",
    stat2_label:"лидеров отрасли доверяют нам",
    stat3_label:"лет в маркетинге",
    cta_proposal:"Получить предложение",
    cta_call:"Заказать звонок",
    trusted_by:"Нам доверяют лидеры рынка",
    online_label:"Онлайн — отвечаем за минуты",
    /* page heroes */
    mp_h1:"Найдите подходящего партнёра<br/>для вашей кампании",
    mp_sub:"Просматривайте надёжных поставщиков услуг — Polymarket, BC.Game, 1win и другие — в Крипто, Форекс и iGaming. Фильтруйте по индустрии, модели выплат и региону.",
    cases_h1:"Результаты, которые<br/>говорят сами за себя",
    cases_hsub:"Реальные кампании. Реальные клиенты. Реальные цифры. Вот что мы сделали в Крипто, Форекс и iGaming.",
    clients_h1:"500+ проектов.<br/>Имена, которые вы знаете.",
    clients_sub:"Мы работали с крупнейшими именами в Крипто, Форекс и iGaming — а также с новым поколением проектов, о которых вы скоро услышите.",
    sv_h1a:"Маркетинг полного цикла",
    sv_h1b:"только для трёх индустрий",
    sv_sub:"Мы специализируемся исключительно на Крипто, Форекс и iGaming. Каждая наша услуга отточена в 50+ реальных кампаниях в этих вертикалях — а не адаптирована из общих маркетинговых шаблонов.",
    ind_h1a:"Глубокая экспертиза в",
    ind_h1b:"высокоставочных индустриях",
    ind_sub:"Мы не занимаемся общим маркетингом. Мы специализируемся исключительно на Крипто, Форекс и iGaming — самых конкурентных, регулируемых и быстрых индустриях в мире. Выберите свою вертикаль ниже.",
    our_services:"Наши Услуги",
    services_title:"Всё, что нужно вашему<br/>проекту для роста",
    services_sub:"Мы охватываем полный стек роста — от построения аудитории и привлечения пользователей до фандрейзинг-кампаний, запуска токенов и Web3-разработки.",
    industries_tag:"Индустрии",
    industries_title:"Создано для рынков высоких ставок",
    industries_sub:"Мы помогаем брендам расти там, где конкуренция беспощадна, доверие достаётся с трудом, а на счету каждый пользователь.",
    cases_tag:"Кейсы",
    cases_title:"Результаты, которыми мы гордимся",
    cases_sub:"Реальные кампании. Реальные клиенты. Реальные цифры.",
    how_tag:"Как мы работаем",
    how_title:"От первого звонка до<br/>измеримого роста",
    how_sub:"Прозрачный процесс для конкурентных рынков — от исследования и стратегии до запуска, отчётности и масштабирования.",
    cta_big_title:"Готовы к росту?",
    cta_big_sub:"Оставьте свои данные — ответим в течение 24 часов с индивидуальной стратегией для вашего проекта.",
    footer_desc:"Полноценное маркетинговое агентство для Web3, Крипто, Форекс и iGaming. Ориентированы на результат. Узкая специализация.",
    nav_growth:"Рост и охват",
    nav_brand:"Бренд и сообщество",
    nav_verticals:"Наши вертикали",
    nav_explore:"Исследовать",
    nav_content:"Типы контента",
    nav_about_col:"О нас",
  },
  es: {
    contactUs:"Contáctanos", marketplace:"Marketplace",
    services:"Servicios", industries:"Industrias", caseStudies:"Casos",
    blog:"Blog", company:"Empresa",
    search_placeholder:"Buscar páginas, servicios, industrias…",
    quick_links:"Accesos rápidos",
    hero_h1_1:"Marketing Todo-en-Uno",
    hero_h1_2:"para tu",
    hero_h1_3:"Crypto, Forex",
    hero_h1_4:"&",
    hero_h1_6:"",
    hero_sub:"Agencia integral y marketplace de afiliados que abarca marketing KOL, PR, gestión de comunidades, recaudación de fondos y desarrollo Web3 — asegura que tu marca destaque y alcance un crecimiento global.",
    stat1_label:"clientes satisfechos",
    stat2_label:"líderes confían en nosotros",
    stat3_label:"años en marketing",
    cta_proposal:"Obtener propuesta gratis",
    cta_call:"Reservar llamada",
    trusted_by:"Confiado por equipos líderes de Web3",
    online_label:"Online — respondemos en minutos",
    /* page heroes */
    mp_h1:"Encuentra el socio ideal<br/>para tu campaña",
    mp_sub:"Explora proveedores de servicios de confianza — Polymarket, BC.Game, 1win y más — en Crypto, Forex e iGaming. Filtra por industria, modelo de pago y región.",
    cases_h1:"Resultados que<br/>hablan por sí solos",
    cases_hsub:"Campañas reales. Clientes reales. Números reales. Esto es lo que hemos logrado en Crypto, Forex y iGaming.",
    clients_h1:"500+ proyectos.<br/>Nombres que conoces.",
    clients_sub:"Hemos trabajado con los nombres más grandes en Crypto, Forex y iGaming, así como con la nueva generación de proyectos de los que pronto oirás hablar.",
    sv_h1a:"Marketing integral",
    sv_h1b:"solo para tres industrias",
    sv_sub:"Nos especializamos exclusivamente en Crypto, Forex y iGaming. Cada servicio que ofrecemos se ha perfeccionado en más de 50 campañas reales en estos sectores, no adaptado de plantillas genéricas.",
    ind_h1a:"Experiencia profunda en",
    ind_h1b:"industrias de alto riesgo",
    ind_sub:"No hacemos marketing general. Nos especializamos exclusivamente en Crypto, Forex y iGaming: las industrias más competitivas, reguladas y dinámicas del mundo. Elige tu sector a continuación.",
    our_services:"Nuestros Servicios",
    services_title:"Todo lo que tu proyecto<br/>necesita para crecer",
    services_sub:"Cubrimos todo el stack de crecimiento — desde la construcción de audiencias y la captación de usuarios hasta campañas de fundraising, lanzamientos de tokens y desarrollo Web3.",
    industries_tag:"Industrias",
    industries_title:"Creados para sectores de alto riesgo",
    industries_sub:"Ayudamos a las marcas a crecer en mercados donde la competencia es feroz, la confianza cuesta ganarla y cada usuario cuenta.",
    cases_tag:"Casos de Éxito",
    cases_title:"Resultados de los que estamos orgullosos",
    cases_sub:"Campañas reales. Clientes reales. Números reales.",
    how_tag:"Cómo trabajamos",
    how_title:"Del primer contacto al<br/>crecimiento medible",
    how_sub:"Un proceso transparente para mercados competitivos: desde la investigación y la estrategia hasta el lanzamiento, los informes y la escala.",
    cta_big_title:"¿Listo para crecer?",
    cta_big_sub:"Deja tus datos y nuestro equipo responderá en 24 horas con una estrategia personalizada.",
    footer_desc:"Agencia de marketing integral para Web3, Crypto, Forex e iGaming. Orientados a resultados. Especialización profunda.",
    nav_growth:"Crecimiento y alcance",
    nav_brand:"Marca y comunidad",
    nav_verticals:"Nuestras verticales",
    nav_explore:"Explorar",
    nav_content:"Tipos de contenido",
    nav_about_col:"Empresa",
  },
  de: {
    contactUs:"Kontakt", marketplace:"Marktplatz",
    services:"Leistungen", industries:"Branchen", caseStudies:"Fallstudien",
    blog:"Blog", company:"Unternehmen",
    search_placeholder:"Seiten, Leistungen, Branchen suchen…",
    quick_links:"Schnellzugriff",
    hero_h1_1:"All-in-One Marketing",
    hero_h1_2:"für Ihr",
    hero_h1_3:"Crypto, Forex",
    hero_h1_4:"&",
    hero_h1_6:"Projekt",
    hero_sub:"Full-Service-Agentur und Affiliate-Marktplatz, der KOL-Marketing, PR, Community-Management, Fundraising und Web3-Entwicklung abdeckt — lassen Sie Ihre Marke herausstechen und erreichen Sie globales Wachstum.",
    stat1_label:"zufriedene Kunden",
    stat2_label:"Branchenführer vertrauen uns",
    stat3_label:"Jahre im Marketing",
    cta_proposal:"Kostenloses Angebot",
    cta_call:"Anruf buchen",
    trusted_by:"Vertraut von führenden Web3-Teams",
    online_label:"Online — wir antworten in Minuten",
    /* page heroes */
    mp_h1:"Finden Sie den richtigen Partner<br/>für Ihre Kampagne",
    mp_sub:"Durchsuchen Sie vertrauenswürdige Dienstleister — Polymarket, BC.Game, 1win und mehr — in Crypto, Forex und iGaming. Filtern Sie nach Branche, Vergütungsmodell und Region.",
    cases_h1:"Ergebnisse, die<br/>für sich sprechen",
    cases_hsub:"Echte Kampagnen. Echte Kunden. Echte Zahlen. Das haben wir in Crypto, Forex und iGaming geliefert.",
    clients_h1:"500+ Projekte.<br/>Namen, die Sie kennen.",
    clients_sub:"Wir haben mit den größten Namen in Crypto, Forex und iGaming gearbeitet — sowie mit der nächsten Generation von Projekten, von denen Sie bald hören werden.",
    sv_h1a:"Full-Spectrum-Marketing",
    sv_h1b:"nur für drei Branchen",
    sv_sub:"Wir spezialisieren uns ausschließlich auf Crypto, Forex und iGaming. Jede unserer Leistungen wurde in über 50 echten Kampagnen in diesen Bereichen verfeinert — nicht aus allgemeinen Marketingvorlagen übernommen.",
    ind_h1a:"Tiefe Expertise in",
    ind_h1b:"hochriskanten Branchen",
    ind_sub:"Wir machen kein allgemeines Marketing. Wir spezialisieren uns ausschließlich auf Crypto, Forex und iGaming — die wettbewerbsintensivsten, reguliertesten und schnelllebigsten Branchen der Welt. Wählen Sie unten Ihren Bereich.",
    our_services:"Unsere Leistungen",
    services_title:"Alles, was Ihr Projekt<br/>zum Wachsen braucht",
    services_sub:"Wir decken den gesamten Growth-Stack ab — vom Zielgruppenaufbau und der Nutzergewinnung bis zu Fundraising-Kampagnen, Token-Launches und Web3-Entwicklung.",
    industries_tag:"Branchen",
    industries_title:"Gemacht für Branchen mit hohem Einsatz",
    industries_sub:"Wir bringen Marken dort zum Wachsen, wo der Wettbewerb erbittert ist, Vertrauen hart erarbeitet werden muss und jeder einzelne Nutzer zählt.",
    cases_tag:"Fallstudien",
    cases_title:"Ergebnisse, auf die wir stolz sind",
    cases_sub:"Echte Kampagnen. Echte Kunden. Echte Zahlen.",
    how_tag:"Wie wir arbeiten",
    how_title:"Vom ersten Anruf zu<br/>messbarem Wachstum",
    how_sub:"Ein transparenter Prozess für umkämpfte Märkte — von Recherche und Strategie bis zu Launch, Reporting und Skalierung.",
    cta_big_title:"Bereit zu wachsen?",
    cta_big_sub:"Hinterlassen Sie Ihre Daten — unser Team meldet sich innerhalb von 24 Stunden mit einer maßgeschneiderten Strategie.",
    footer_desc:"Full-Spectrum-Marketingagentur für Web3, Crypto, Forex und iGaming. Ergebnisorientiert. Branchenspezialisiert.",
    nav_growth:"Wachstum & Reichweite",
    nav_brand:"Marke & Community",
    nav_verticals:"Unsere Vertikalen",
    nav_explore:"Erkunden",
    nav_content:"Inhaltstypen",
    nav_about_col:"Über uns",
  },
  pt: {
    contactUs:"Contato", marketplace:"Marketplace",
    services:"Serviços", industries:"Indústrias", caseStudies:"Casos",
    blog:"Blog", company:"Empresa",
    search_placeholder:"Pesquisar páginas, serviços, indústrias…",
    quick_links:"Links rápidos",
    hero_h1_1:"Marketing All-in-One",
    hero_h1_2:"para o seu",
    hero_h1_3:"Crypto, Forex",
    hero_h1_4:"&",
    hero_h1_6:"projeto",
    hero_sub:"Agência full-cycle e marketplace de afiliados que abrange marketing KOL, PR, gestão de comunidade, fundraising e desenvolvimento Web3 — faça a sua marca destacar-se e alcançar crescimento global.",
    stat1_label:"clientes satisfeitos",
    stat2_label:"líderes do setor confiam em nós",
    stat3_label:"anos em marketing",
    cta_proposal:"Proposta gratuita",
    cta_call:"Agendar chamada",
    trusted_by:"Confiado por equipas líderes de Web3",
    online_label:"Online — respondemos em minutos",
    mp_h1:"Encontre o parceiro certo<br/>para a sua campanha",
    mp_sub:"Explore prestadores de serviços de confiança — Polymarket, BC.Game, 1win e mais — em Crypto, Forex e iGaming. Filtre por indústria, modelo de pagamento e região.",
    cases_h1:"Resultados que<br/>falam por si",
    cases_hsub:"Campanhas reais. Clientes reais. Números reais. Eis o que entregámos em Crypto, Forex e iGaming.",
    clients_h1:"500+ projetos.<br/>Nomes que conhece.",
    clients_sub:"Trabalhámos com os maiores nomes em Crypto, Forex e iGaming — e também com a nova geração de projetos de que ouvirá falar em breve.",
    sv_h1a:"Marketing de espectro total",
    sv_h1b:"apenas para três indústrias",
    sv_sub:"Especializamo-nos exclusivamente em Crypto, Forex e iGaming. Cada serviço que oferecemos foi aperfeiçoado em mais de 50 campanhas reais nestes setores — não adaptado de modelos genéricos.",
    ind_h1a:"Experiência profunda em",
    ind_h1b:"indústrias de alto risco",
    ind_sub:"Não fazemos marketing genérico. Especializamo-nos exclusivamente em Crypto, Forex e iGaming — as indústrias mais competitivas, reguladas e dinâmicas do mundo. Escolha o seu setor abaixo.",
    our_services:"Os nossos serviços",
    services_title:"Tudo o que o seu projeto<br/>precisa para crescer",
    services_sub:"Cobrimos toda a stack de crescimento — da construção de audiência e aquisição de usuários a campanhas de fundraising, lançamentos de tokens e desenvolvimento Web3.",
    industries_tag:"Indústrias",
    industries_title:"Feitos para setores de alto risco",
    industries_sub:"Ajudamos marcas a crescer em mercados onde a concorrência é feroz, a confiança custa a conquistar e cada utilizador conta.",
    cases_tag:"Casos de sucesso",
    cases_title:"Resultados de que nos orgulhamos",
    cases_sub:"Campanhas reais. Clientes reais. Números reais.",
    how_tag:"Como trabalhamos",
    how_title:"Da primeira chamada ao<br/>crescimento mensurável",
    how_sub:"Um processo transparente para mercados competitivos — da pesquisa e estratégia ao lançamento, relatórios e escala.",
    cta_big_title:"Pronto para crescer?",
    cta_big_sub:"Deixe os seus dados — a nossa equipa responde em 24 horas com uma estratégia personalizada.",
    footer_desc:"Agência de marketing de espectro total para Web3, Crypto, Forex e iGaming. Orientada a resultados. Especializada por setor.",
    nav_growth:"Crescimento e alcance",
    nav_brand:"Marca e comunidade",
    nav_verticals:"As nossas verticais",
    nav_explore:"Explorar",
    nav_content:"Tipos de conteúdo",
    nav_about_col:"Empresa",
  },
  ar: {
    contactUs:"تواصل معنا",
    marketplace:"المتجر",
    services:"الخدمات",
    industries:"القطاعات",
    caseStudies:"دراسات الحالة",
    blog:"المدونة",
    company:"الشركة",
    search_placeholder:"ابحث في الصفحات والخدمات والقطاعات…",
    quick_links:"روابط سريعة",
    hero_h1_1:"تسويق متكامل في منصة واحدة",
    hero_h1_2:"لمشروعك في",
    hero_h1_3:"Crypto وForex",
    hero_h1_4:"&",
    hero_h1_6:"مشروعك",
    hero_sub:"وكالة متكاملة الدورة ومتجر تسويق بالعمولة يغطي تسويق KOL وPR وإدارة المجتمعات وجمع التمويل وتطوير Web3 — اجعل علامتك التجارية تتميز وتحقق نمواً عالمياً.",
    stat1_label:"عميل راضٍ",
    stat2_label:"رواد القطاع يثقون بنا",
    stat3_label:"عاماً في مجال التسويق",
    cta_proposal:"احصل على عرض مجاني",
    cta_call:"احجز مكالمة",
    trusted_by:"موضع ثقة الشركات الرائدة في القطاع",
    online_label:"متصلون الآن — نرد خلال دقائق",
    mp_h1:"اعثر على الشريك المناسب<br/>لحملتك",
    mp_sub:"تصفّح مزوّدي الخدمات الموثوقين — Polymarket وBC.Game و1win وغيرهم — في Crypto وForex وiGaming. صفّ النتائج حسب القطاع ونموذج الدفع والمنطقة.",
    cases_h1:"نتائج تتحدث<br/>عن نفسها",
    cases_hsub:"حملات حقيقية. عملاء حقيقيون. أرقام حقيقية. إليك ما حققناه في Crypto وForex وiGaming.",
    clients_h1:"أكثر من 500 مشروع.<br/>بعض الأسماء تعرفها.",
    clients_sub:"عملنا مع أكبر الأسماء في Crypto وForex وiGaming — إلى جانب الجيل القادم من المشاريع التي ستسمع عنها قريباً.",
    sv_h1a:"تسويق شامل بكل أبعاده",
    sv_h1b:"لثلاثة قطاعات فقط",
    sv_sub:"نتخصص حصرياً في Crypto وForex وiGaming. كل خدمة نقدّمها صُقلت عبر أكثر من 50 حملة حقيقية في هذه المجالات — لا قوالب تسويقية عامة مُكيّفة.",
    ind_h1a:"خبرة عميقة في",
    ind_h1b:"قطاعات عالية المخاطر",
    ind_sub:"نحن لا نقدّم تسويقاً عاماً. نتخصص حصرياً في Crypto وForex وiGaming — أكثر القطاعات تنافسية وتنظيماً وسرعةً في العالم. اختر مجالك أدناه.",
    our_services:"خدماتنا",
    services_title:"كل ما يحتاجه مشروعك<br/>للنمو",
    services_sub:"نغطّي منظومة النمو بالكامل — من بناء الجمهور واكتساب المستخدمين إلى حملات جمع التمويل وإطلاق الرموز وتطوير Web3.",
    industries_tag:"القطاعات",
    industries_title:"مصمّمون للقطاعات عالية المخاطر",
    industries_sub:"نساعد العلامات على النمو في أسواقٍ تشتدّ فيها المنافسة، ويصعب فيها كسب الثقة، ويكون فيها لكل مستخدمٍ أهميته.",
    cases_tag:"دراسات الحالة",
    cases_title:"نتائج نفخر بها",
    cases_sub:"حملات حقيقية. عملاء حقيقيون. أرقام حقيقية.",
    how_tag:"كيف نعمل",
    how_title:"من أول مكالمة إلى<br/>نموّ قابل للقياس",
    how_sub:"عملية شفّافة مصمّمة للأسواق التنافسية — من البحث والاستراتيجية إلى الإطلاق والتقارير والتوسّع.",
    cta_big_title:"هل أنت مستعد لتنمية مشروعك؟",
    cta_big_sub:"اترك بياناتك وسيرد فريقنا خلال 24 ساعة باستراتيجية مصممة خصيصاً لمشروعك.",
    footer_desc:"وكالة تسويق شاملة بكل أبعادها لـ Web3 وCrypto وForex وiGaming. مدفوعة بالنتائج. متخصصة في القطاع.",
    nav_growth:"النمو & الانتشار",
    nav_brand:"العلامة التجارية & المجتمع",
    nav_verticals:"مجالاتنا",
    nav_explore:"استكشف",
    nav_content:"أنواع المحتوى",
    nav_about_col:"من نحن",
  },
};

export function t(lang, key) { return T[lang]?.[key] ?? T.en[key] ?? key; }

const _FLAG_LANGS = [
  { code:"en", label:"English", Flag: () => (
    <svg width="28" height="19" viewBox="0 0 28 19" style={{ borderRadius:3, display:"block", flexShrink:0 }}>
      <rect width="28" height="19" fill="#B22234"/>
      <rect y="1.46" width="28" height="1.46" fill="#fff"/><rect y="2.92" width="28" height="1.46" fill="#B22234"/>
      <rect y="4.38" width="28" height="1.46" fill="#fff"/><rect y="5.84" width="28" height="1.46" fill="#B22234"/>
      <rect y="7.3" width="28" height="1.46" fill="#fff"/><rect y="8.77" width="28" height="1.46" fill="#B22234"/>
      <rect y="10.23" width="28" height="1.46" fill="#fff"/><rect y="11.69" width="28" height="1.46" fill="#B22234"/>
      <rect y="13.15" width="28" height="1.46" fill="#fff"/><rect y="14.61" width="28" height="1.46" fill="#B22234"/>
      <rect y="16.08" width="28" height="1.46" fill="#fff"/><rect y="17.54" width="28" height="1.46" fill="#B22234"/>
      <rect width="11" height="10.23" fill="#3C3B6E"/>
      {Array.from({ length: 4 }).flatMap((_, r) =>
        Array.from({ length: 5 }).map((__, c) => (
          <circle key={`${r}-${c}`} cx={1.5 + c * 2} cy={1.5 + r * 2.2} r="0.36" fill="#fff" />
        ))
      )}
    </svg>
  )},
  { code:"uk", label:"Українська", Flag: () => (
    <svg width="28" height="19" viewBox="0 0 28 19" style={{ borderRadius:3, display:"block", flexShrink:0 }}>
      <rect width="28" height="19" fill="#FFD700"/>
      <rect width="28" height="9.5" fill="#0057B7"/>
    </svg>
  )},
  { code:"ru", label:"Русский", Flag: () => (
    <svg width="28" height="19" viewBox="0 0 28 19" style={{ borderRadius:3, display:"block", flexShrink:0 }}>
      <rect width="28" height="19" fill="#D52B1E"/>
      <rect width="28" height="12.67" fill="#0039A6"/>
      <rect width="28" height="6.33" fill="#fff"/>
    </svg>
  )},
  { code:"es", label:"Español", Flag: () => (
    <svg width="28" height="19" viewBox="0 0 28 19" style={{ borderRadius:3, display:"block", flexShrink:0 }}>
      {/* official 1:2:1 red–yellow–red bands */}
      <rect width="28" height="19" fill="#AA151B"/>
      <rect y="4.75" width="28" height="9.5" fill="#F1BF00"/>
      {/* simplified coat of arms at ~1/3 from the hoist */}
      <g fill="#AA151B" stroke="#7d0f12" strokeWidth="0.25">
        <rect x="6.2" y="6.7" width="0.85" height="5.6" rx="0.42" stroke="none"/>
        <rect x="11.3" y="6.7" width="0.85" height="5.6" rx="0.42" stroke="none"/>
        <path d="M7.7 6.6 h3 v2.5 q0 2 -1.5 2.7 q-1.5 -0.7 -1.5 -2.7 z"/>
        <path d="M8.1 6.0 h2.2 l-0.3 -1 -0.45 0.55 -0.35 -0.8 -0.35 0.8 -0.45 -0.55 z" stroke="none"/>
      </g>
    </svg>
  )},
  { code:"de", label:"Deutsch", Flag: () => (
    <svg width="28" height="19" viewBox="0 0 28 19" style={{ borderRadius:3, display:"block", flexShrink:0 }}>
      <rect width="28" height="19" fill="#FFCE00"/>
      <rect width="28" height="12.67" fill="#DD0000"/>
      <rect width="28" height="6.33" fill="#000"/>
    </svg>
  )},
  { code:"pt", label:"Português", Flag: () => (
    <svg width="28" height="19" viewBox="0 0 28 19" style={{ borderRadius:3, display:"block", flexShrink:0 }}>
      <rect width="28" height="19" fill="#FF0000"/>
      <rect width="11.2" height="19" fill="#006600"/>
      {/* armillary sphere + shield at the green/red boundary */}
      <g transform="translate(11.2,9.5)">
        <g fill="none" stroke="#FFD700" strokeWidth="0.5">
          <circle r="3.1"/>
          <ellipse rx="3.1" ry="1.25"/>
          <ellipse rx="1.35" ry="3.1"/>
        </g>
        <path d="M-1.25 -1.85 h2.5 v1.95 q0 1.35 -1.25 1.8 q-1.25 -0.45 -1.25 -1.8 z" fill="#fff" stroke="#c1121f" strokeWidth="0.4"/>
      </g>
    </svg>
  )},
  { code:"ar", label:"العربية", Flag: () => (
    <svg width="28" height="19" viewBox="0 0 28 19" style={{ borderRadius:3, display:"block", flexShrink:0 }}>
      <rect width="28" height="6.33" fill="#009639"/>
      <rect y="6.33" width="28" height="6.34" fill="#fff"/>
      <rect y="12.67" width="28" height="6.33" fill="#000"/>
      <rect width="7" height="19" fill="#FF0000"/>
    </svg>
  )},
];

// Display order for the switcher (dropdown + footer): US · Spain · Russia · Germany · Ukraine · Portugal · Arabic
export const FLAG_LANGS = ["en", "es", "ru", "de", "uk", "pt", "ar"]
  .map(c => _FLAG_LANGS.find(l => l.code === c))
  .filter(Boolean);
